import {
  ACTIVE_LOCATIONS,
  ActivePlace,
  AutocompletePrediction,
  Coordinates,
  isActiveCountry,
  LocationFullType,
  Place,
} from './config';
import flatMap from 'lodash/flatMap';

// Function to calculate the Haversine distance in kilometers
function haversine(lat1: number, lon1: number, lat2: number, lon2: number) {
  const R = 6371;
  const phi1 = (lat1 * Math.PI) / 180;
  const phi2 = (lat2 * Math.PI) / 180;
  const deltaPhi = ((lat2 - lat1) * Math.PI) / 180;
  const deltaLambda = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(deltaPhi / 2) ** 2 +
    Math.cos(phi1) * Math.cos(phi2) * Math.sin(deltaLambda / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}

export const getNearestPlace = <T extends Place | ActivePlace>(
  places: T[],
  coordinates: Coordinates,
): { closestPlace: T | null; closestDistance: number } => {
  let closestPlace: T | null = null;
  let closestDistance = Infinity;

  for (let p of places) {
    const distance = haversine(
      coordinates.lat,
      coordinates.lng,
      p.location.lat,
      p.location.lng,
    );
    if (distance < closestDistance) {
      closestDistance = distance;
      closestPlace = p;
    }
  }

  return {
    closestPlace,
    closestDistance,
  };
};

export const getCountryInfo = (results: google.maps.GeocoderResult[]) => {
  // 1
  let country = '';
  let countryCode = '';
  const countryComponent = results[0].address_components.find(d =>
    d.types.includes('country'),
  );
  if (countryComponent) {
    country = countryComponent.long_name;
    countryCode = countryComponent.short_name;
  }

  // 2
  let state = '';
  let stateCode = '';
  const stateComponent = results[0].address_components.find(d =>
    d.types.includes('administrative_area_level_1'),
  );
  if (stateComponent) {
    state = stateComponent.long_name;
    stateCode = stateComponent.short_name;
  }

  // 3
  let city = '';
  let cityCode = '';
  const localityComponent = results[0].address_components.find(d =>
    d.types.includes('locality'),
  );
  if (localityComponent) {
    city = localityComponent.long_name;
    cityCode = localityComponent.short_name;
  }

  // For islands and geography areas
  if (!city) {
    const naturalFeatureComponent = results[0].address_components.find(d =>
      d.types.includes('natural_feature'),
    );
    if (naturalFeatureComponent) city = naturalFeatureComponent?.long_name;
  }

  return {
    country,
    state,
    city,
  };
};

// Google API

export const getGeocodeResults = (
  request: google.maps.GeocoderRequest,
): Promise<google.maps.GeocoderResult[]> => {
  return new Promise((resolve, reject) => {
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode(
      { ...request, language: 'en' },
      async (results, status) => {
        if (status !== 'OK' || !results?.[0]) {
          return reject(new Error('Can not get geocode results.'));
        }

        return resolve(results);
      },
    );
  });
};

export const getAutocomplePredictions = (
  placeName: string,
  allowAll?: boolean,
): Promise<AutocompletePrediction[]> => {
  return new Promise((resolve, reject) => {
    const service = new google.maps.places.AutocompleteService();
    service.getPlacePredictions(
      {
        input: placeName,
        types: !allowAll ? ['(cities)'] : undefined,
      },
      (predictions, status) => {
        if (status !== 'OK' || !predictions?.length) {
          return reject(new Error('Can not get autocomplete prediction.'));
        }

        return resolve(predictions);
      },
    );
  });
};

export const getNearestActiveLocation = ({
  coordinates,
  country,
}: Pick<LocationFullType, 'coordinates' | 'country'>) => {
  const places = isActiveCountry(country)
    ? ACTIVE_LOCATIONS[country].places
    : flatMap(ACTIVE_LOCATIONS, a => a.places).filter(l => l.isPopularOutside);

  const { closestPlace, closestDistance } = getNearestPlace(
    places,
    coordinates,
  );

  if (!closestPlace)
    throw new Error('Not possible to find nearest active location.');

  return {
    nearestActiveLocation: getLocationFromActivePlace(closestPlace),
    nearestActiveLocationSeoLine: closestPlace.seoLine,
    nearestDistance: closestDistance,
  };
};

export const getLocationFromActivePlace = (place: ActivePlace) => {
  const city = place.data.city;
  const state = place.data.state;
  const country = place.data.country;

  const location: LocationFullType = {
    coordinates: place.location,
    place_id: place.placeId,
    city,
    state,
    country,
    inputMain: city || state,
    inputSecondary: country,
    description: (city || state) + ', ' + country,
    structured_formatting: {
      main_text: city || state,
      secondary_text: country,
      main_text_matched_substrings: [],
    },
    matched_substrings: [],
    terms: [],
    types: [],
  };

  return location;
};
